// src/portal/PortalThread.jsx
//
// The conversation. Route 7 to read, route 8 to write.
//
// ── TWO RULES, ONE SCREEN, BOTH STATED ──────────────────────────────────────
//
// Notes are visible on write. Markup is frozen at return. Those look alike and
// are not, and a submitter who assumes the first rule governs both will read a
// quiet thread as "the reviewer has said nothing" when the reviewer has written
// six findings they cannot see yet. So the note box says which rule it is
// under, next to the box, where someone about to write will read it.
//
// ── AND WHO CAN SEE A NOTE, NEXT TO THE BOX ─────────────────────────────────
//
// A submitter is a third party writing into the customer's organisation. The
// audience is not obvious from the UI and guessing wrong in either direction is
// bad: assuming privacy and being read, or assuming an audience and staying
// silent. So it is stated, and the wording comes from the viewer's role.
//
// ── POLLED, NOT STREAMED ────────────────────────────────────────────────────
//
// /api/v1/collab/stream is off the portal class's allowlist and stays off:
// EventSource cannot send headers, so it takes its bearer token as a QUERY
// PARAMETER, and handing a third party that shape is a larger surface than a
// poll. 30s matches App.jsx's notification interval.
//
// NO REPLY AFFORDANCE IN v1. The table supports one level and route 7 renders
// any replies that exist, so a thread created another way still displays -- but
// the portal does not offer the control. A two-party conversation gains nothing
// from threading, and it is one less thing to decide about who gets told.
import { useCallback, useEffect, useRef, useState } from "react";
import { getThread, addNote, PortalError } from "../api/portalApi";

const POLL_MS = 30_000;

export default function PortalThread({ planReviewId, viewerRole, disabled, disabledReason }) {
  const [events, setEvents] = useState(null);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const timer = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await getThread(planReviewId);
      setEvents(data.events);
      setError(null);
    } catch (e) {
      setError(e instanceof PortalError ? e : new PortalError(0, { error: String(e) }));
    }
  }, [planReviewId]);

  useEffect(() => {
    load();
    timer.current = setInterval(() => {
      // Don't poll a tab nobody is looking at. The thread is not urgent and a
      // background tab polling every 30s for a day is a lot of nothing.
      if (document.visibilityState === "visible") load();
    }, POLL_MS);
    return () => clearInterval(timer.current);
  }, [load]);

  async function send(e) {
    e.preventDefault();
    if (!draft.trim() || sending || disabled) return;
    setSending(true);
    try {
      await addNote(planReviewId, { body: draft.trim() });
      setDraft("");
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setSending(false);
    }
  }

  const audience = viewerRole === "submitter"
    ? "the reviewing organization"
    : "the submitter";

  return (
    <section className="flex min-h-0 flex-col gap-3">
      <h2 className="text-sm font-medium">Conversation</h2>

      {error && (
        <p className="text-xs text-red-400">
          {error.message}{error.code ? ` (${error.code})` : ""}
        </p>
      )}

      <ol className="flex flex-col gap-2 overflow-auto">
        {events === null && <li className="text-xs text-fg-secondary">Loading…</li>}
        {events?.length === 0 && (
          <li className="text-xs text-fg-secondary">Nothing said yet.</li>
        )}
        {events?.map((n) => (
          <li key={n.id} className="rounded border border-bg-border px-3 py-2">
            <div className="flex items-baseline gap-2">
              <span className="text-xs font-medium">{n.author_display ?? "Unknown"}</span>
              <span className="text-[11px] text-fg-secondary">
                {new Date(n.created_at).toLocaleString()}
              </span>
            </div>
            <p className="mt-1 whitespace-pre-wrap text-sm">{n.body}</p>
            {n.replies?.length > 0 && (
              <ol className="mt-2 flex flex-col gap-2 border-l border-bg-border pl-3">
                {n.replies.map((r) => (
                  <li key={r.id}>
                    <div className="flex items-baseline gap-2">
                      <span className="text-xs font-medium">{r.author_display ?? "Unknown"}</span>
                      <span className="text-[11px] text-fg-secondary">
                        {new Date(r.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="mt-1 whitespace-pre-wrap text-sm">{r.body}</p>
                  </li>
                ))}
              </ol>
            )}
          </li>
        ))}
      </ol>

      {disabled ? (
        <p className="text-xs text-fg-secondary">{disabledReason}</p>
      ) : (
        <form onSubmit={send} className="flex flex-col gap-1">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            maxLength={4000}
            placeholder="Write a note…"
            className="rounded border border-bg-border bg-bg-soft px-3 py-2 text-sm"
          />
          {/* THE TWO SENTENCES THIS COMPONENT EXISTS TO PUT ON THE SCREEN. */}
          <p className="text-[11px] text-fg-secondary">
            Notes are visible to <strong>{audience}</strong> as soon as you post them.
            Review findings on the map are not — those appear when the reviewer returns the plan.
          </p>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={!draft.trim() || sending}
              className="rounded border border-bg-border px-3 py-1.5 text-sm disabled:opacity-40"
            >
              {sending ? "Posting…" : "Post note"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}
